import React from 'react';
import { Divider, Typography } from 'antd';
// Helpers
import {
  formatCount,
  formatMoney,
  formatTimezoneName,
  getTiersSummary,
  TierDraft,
} from 'pages/Events/helpers';
// Styles
import styles from './EventSummary.module.scss';

const { Text } = Typography;

type EventSummaryProps = {
  ticketsEnabled: boolean;
  tiers: TierDraft[];
  savedTiers?: TierDraft[];
  maxAdmissions?: number | null;
  timezone?: string;
  serviceFeePercent?: number;
  serviceFeePerTicket?: number;
  affiliatePercent?: number | null;
  affiliateEarnings?: string | null;
  outcome: string;
};

const Row = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element => (
  <div className={styles.row}>
    <Text type="secondary">{label}</Text>
    <Text strong>{value}</Text>
  </div>
);

const EventSummary = ({
  ticketsEnabled,
  tiers,
  savedTiers = [],
  maxAdmissions,
  timezone,
  serviceFeePercent = 0,
  serviceFeePerTicket = 0,
  affiliatePercent,
  affiliateEarnings,
  outcome,
}: EventSummaryProps): JSX.Element => {
  const summary = getTiersSummary([...savedTiers, ...tiers]);
  const hasFees = serviceFeePercent > 0 || serviceFeePerTicket > 0;

  return (
    <aside className={styles.root} aria-label="Event summary">
      <Text className={styles.title}>Summary</Text>
      <Row label="Ticketing" value={ticketsEnabled ? 'On' : 'Off'} />
      {ticketsEnabled && (
        <>
          <Row label="Tiers" value={summary.count} />
          <Row
            label="Tickets available"
            value={formatCount(summary.ticketsAvailable)}
          />
          <Row
            label="Capacity"
            value={formatCount(maxAdmissions || summary.ticketsAvailable)}
          />
          <Row
            label="Max ticket revenue"
            value={formatMoney(summary.maxRevenue)}
          />
          <Text type="secondary" className={styles.note}>
            {hasFees
              ? `Buyers also pay the service fee (${serviceFeePercent}% + ${formatMoney(
                  serviceFeePerTicket
                )} per ticket), which isn't part of the host's revenue.`
              : 'No service fee is charged on top of ticket prices.'}
          </Text>
        </>
      )}
      <Divider className={styles.divider} />
      <Row label="Affiliate program" value={affiliateEarnings ? 'On' : 'Off'} />
      {affiliateEarnings && (
        <>
          <Row label="Commission" value={`${affiliatePercent ?? 0}%`} />
          <Row
            label="Participant earns"
            value={`${affiliateEarnings} / sale`}
          />
        </>
      )}
      <Divider className={styles.divider} />
      <Row
        label="Time zone"
        value={timezone ? formatTimezoneName(timezone) : 'Pick a venue'}
      />
      <Row label="After saving" value={outcome} />
    </aside>
  );
};

export default EventSummary;
